import {Component, HostListener, OnInit, ViewChild} from '@angular/core';
import {OverlayPanel} from 'primeng/overlaypanel';
import {LazyLoadEvent} from 'primeng/api';
import {User} from '../../models/user';
import Swal from 'sweetalert2';
import {UserService} from '../../services/user/user.service';
import {CompanyService} from '../../services/company/company.service';
import {Company} from '../../models/company';
import {UserGroup} from '../../models/userGroup';
import {Router} from '@angular/router';

@Component({
    selector: 'app-admin-allusers',
    templateUrl: './admin-allusers.component.html',
    styleUrls: ['./admin-allusers.component.scss']
})
export class AdminAllusersComponent implements OnInit {
    @ViewChild('filtersPanel') filtersPanel?: OverlayPanel;

    users: User[] = [];
    totalRecords = 0;
    loading = true;
    first = 0;
    rows = 30;

    textFilter = '';
    selectedCompanyIds: string[] = [];
    selectedTypeIds: string[] = [];
    selectedStatusIds: string[] = [];
    sortBy = 'firstName asc';
    noCompany = false;
    companies: Company[] = [];
    typesOfAccout: UserGroup[] = [];
    readonly statusOptions = [
        {id: 'active', name: 'Attivo'},
        {id: 'pendingOtp', name: 'OTP da completare'},
        {id: 'pendingOnboardingOtp', name: 'OTP onboarding'},
        {id: 'pendingOnboarding', name: 'Onboarding in corso'},
        {id: 'pendingApprovation', name: 'In attesa di approvazione'},
    ];
    readonly sortOptions = [
        {id: 'firstName asc', name: 'Nome A–Z'},
        {id: 'lastName asc', name: 'Cognome A–Z'},
        {id: 'createdAt desc', name: 'Più recenti'},
        {id: 'createdAt asc', name: 'Più vecchi'},
        {id: 'lastAccess desc', name: 'Ultimo accesso'},
    ];
    skeletonRows = [0, 1, 2, 3, 4, 5, 6, 7];

    searchMode: 'user' | 'athlete' = 'user';
    readonly searchModes = [
        {id: 'user' as const, label: 'Utente'},
        {id: 'athlete' as const, label: 'Atleta'},
    ];
    private lastLazyEvent: LazyLoadEvent = {first: 0, rows: 30};

    constructor(
        private userService: UserService,
        private companyService: CompanyService,
        private router: Router,
    ) {
    }

    async ngOnInit() {
        this.typesOfAccout = await this.userService.typeOfAccount();
        this.companies = await this.companyService.getCompanies({$orderby: 'name asc'});
    }

    get activeFilterCount(): number {
        return [
            this.selectedTypeIds.length,
            this.selectedCompanyIds.length,
            this.selectedStatusIds.length,
            this.noCompany,
            this.sortBy !== 'firstName asc',
        ].filter(Boolean).length;
    }

    get hasActiveQuery(): boolean {
        return !!this.textFilter.trim() || this.activeFilterCount > 0;
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
        if (!this.filtersPanel?.overlayVisible) return;
        const target = event.target as HTMLElement | null;
        if (!target) return;
        if (
            target.closest('.user-filter-popover') ||
            target.closest('.user-filter-trigger') ||
            target.closest('.p-dropdown-panel') ||
            target.closest('.p-multiselect-panel')
        ) {
            return;
        }
        this.filtersPanel.hide();
    }

    async loadUsers(event?: LazyLoadEvent) {
        if (event) {
            this.lastLazyEvent = event;
            this.first = event.first ?? 0;
            this.rows = event.rows ?? 30;
        }

        const first = this.lastLazyEvent.first ?? 0;
        const rows = this.lastLazyEvent.rows ?? 30;
        const params: Record<string, string | number> = {
            $skip: first,
            $top: rows,
            withAthletes: 'true',
        };

        if (this.textFilter.trim()) params[this.searchMode === 'athlete' ? 'athleteText' : 'text'] = this.textFilter.trim();
        if (!this.noCompany && this.selectedCompanyIds.length) params['companyId'] = this.selectedCompanyIds.join(',');
        if (this.selectedTypeIds.length) params['userGroupId'] = this.selectedTypeIds.join(',');
        if (this.selectedStatusIds.length) params['status'] = this.selectedStatusIds.join(',');
        if (this.noCompany) params['noCompany'] = 'true';
        if (this.sortBy) params['$orderby'] = this.sortBy;

        this.loading = true;
        this.users = [];
        const response = await this.userService.getUsers(params);
        this.users = (response.data ?? []).map((item) => new User(item));
        this.totalRecords = response.count ?? this.users.length;
        this.loading = false;
    }

    applyFilters() {
        this.first = 0;
        this.lastLazyEvent = {...this.lastLazyEvent, first: 0};
        this.filtersPanel?.hide();
        void this.loadUsers(this.lastLazyEvent);
    }

    onNoCompanyChange(enabled: boolean) {
        if (enabled) this.selectedCompanyIds = [];
    }

    clearAdvancedFilters() {
        this.selectedCompanyIds = [];
        this.selectedTypeIds = [];
        this.selectedStatusIds = [];
        this.sortBy = 'firstName asc';
        this.noCompany = false;
        this.applyFilters();
    }

    resetAll() {
        this.textFilter = '';
        this.clearAdvancedFilters();
    }

    accountTypeLabel(userGroupId?: string) {
        return this.typesOfAccout.find(type => type.id === userGroupId)?.name || userGroupId || '—';
    }

    accountTypeIcon(userGroupId?: string): string {
        switch (userGroupId?.toLowerCase()) {
            case 'admin': return 'bi-shield-lock-fill';
            case 'agent': return 'bi-person-badge';
            case 'companymanager': return 'bi-building';
            case 'mister': return 'bi-clipboard2-check';
            case 'user': return 'bi-person';
            default: return 'bi-person-circle';
        }
    }

    statusLabel(status?: string) {
        switch (status) {
            case 'active': return 'Attivo';
            case 'pendingOtp': return 'OTP da completare';
            case 'pendingOnboardingOtp': return 'OTP onboarding';
            case 'pendingOnboarding': return 'Onboarding in corso';
            case 'pendingApprovation': return 'In attesa di approvazione';
            default: return status || '—';
        }
    }

    statusBadgeClass(status?: string) {
        if (status === 'active') return 'badge-success';
        if (status === 'pendingOtp' || status === 'pendingOnboardingOtp') return 'badge-warning';
        if (status === 'pendingOnboarding' || status === 'pendingApprovation') return 'badge-warning';
        return 'badge-unknown';
    }

    statusIcon(status?: string): string {
        switch (status) {
            case 'active': return 'bi-check-circle-fill';
            case 'pendingOtp': return 'bi-shield-lock';
            case 'pendingOnboardingOtp': return 'bi-shield-exclamation';
            case 'pendingOnboarding': return 'bi-hourglass-split';
            case 'pendingApprovation': return 'bi-clock-history';
            default: return 'bi-question-circle';
        }
    }

    initials(user?: User | null) {
        const first = (user?.firstName || '').trim().charAt(0);
        const last = (user?.lastName || '').trim().charAt(0);
        return (first + last).toUpperCase() || (user?.email || '').trim().charAt(0).toUpperCase() || '?';
    }

    setSearchMode(mode: 'user' | 'athlete') {
        this.searchMode = mode;
        if (this.textFilter.trim()) this.applyFilters();
    }

    athleteNames(user: User): string {
        return (user.athletes ?? []).map((athlete: {firstName: string; lastName: string}) => `${athlete.firstName} ${athlete.lastName}`).join(', ');
    }

    openUser(row: User) {
        if (row.id) this.router.navigate(['/admins/allUsers', row.id]);
    }

    async loginAs(email: string, userId?: string) {
        if (!userId) return;
        try {
            const response: any = await this.userService.loginAs(email, userId);
            if (!response.success) {
                let message = 'Errore generico';
                if (response.code === 'USER_NOT_FOUND') message = 'Utente non trovato';
                if (response.code === 'INCORRECT_PASSWORD') message = 'Password errata';
                Swal.fire({icon: 'error', title: message});
            } else {
                setTimeout(() => window.location.reload(), 300);
            }
        } catch (e) {
            console.error('Login error: ', e);
        }
    }

    async deleteUser(id?: string) {
        if (!id) return;
        const result = await Swal.fire({
            icon: 'question',
            title: "Sei sicuro di voler eliminare l'utente?",
            text: 'Questa operazione non può essere annullata',
            showCancelButton: true,
            confirmButtonText: 'Sì, procedi',
            cancelButtonText: "Annulla l'operazione"
        });

        if (!result.isConfirmed) return;

        try {
            const response: any = await this.userService.deleteUser(id);
            if (response.success) {
                Swal.fire({icon: 'success', title: 'Utente eliminato con successo'});
                void this.loadUsers(this.lastLazyEvent);
            } else {
                Swal.fire({icon: 'error', title: "Errore nell'eliminazione dell'utente"});
            }
        } catch (error) {
            Swal.fire({icon: 'error', title: "Errore durante l'eliminazione dell'utente"});
        }
    }
}
